import { Box } from '@mui/material'
import { ThemeProvider, useTheme } from './contexts/ThemeContext'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Sidebar } from './components/Sidebar'
import { Header, DASHBOARD_MODES } from './components/Header/Header'
import { Dashboard } from './pages/Dashboard'
import { Settings } from './pages/Settings'
import PropensityPredictions from './pages/PropensityPredictions'
import { ExcelUpload } from './pages/ExcelUpload'
import { useState, useEffect, useRef, useCallback, createContext, useContext, useMemo, memo } from 'react'
import axiosInstance from './utils/axios'
import { Provider } from 'react-redux'
import { store } from './store/store'
import { DashboardViewProvider } from './contexts/DashboardViewContext'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { FilterVisibilityProvider, useFilterVisibility } from './contexts/FilterVisibilityContext'
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles'
import { logLastApiCall } from './utils/axios'
import { LoadingProvider } from './contexts/LoadingContext'
import { LoadingOverlay } from './components/ui/LoadingOverlay'
import { useIdleTimer } from 'react-idle-timer'

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#003087',
    },
    secondary: {
      main: '#E31837',
    },
  },
})


// Create a context for dashboard view mode
interface DashboardViewContextType {
  viewMode: string;
  setViewMode: (mode: string) => void;
}

export const DashboardViewContext = createContext<DashboardViewContextType>({
  viewMode: DASHBOARD_MODES.STANDARD,
  setViewMode: () => {}
});

// Hook for using the dashboard view context
export function useDashboardView() {
  return useContext(DashboardViewContext);
}


// Memoize the MainContent component to prevent unnecessary re-renders
const MainContent = memo(function MainContent() {
  const { mode, toggleColorMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState(DASHBOARD_MODES.STANDARD);
  const { showFilters, toggleFilters } = useFilterVisibility();
  
  const toggleDrawer = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const dashboardViewValue = useMemo(() => ({
    viewMode,
    setViewMode
  }), [viewMode]);

  return (
    <DashboardViewContext.Provider value={dashboardViewValue}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100vh',
        overflow: 'hidden'
      }}>
        <Header 
          isOpen={isOpen}
          toggleDrawer={toggleDrawer}
          mode={mode}
          toggleColorMode={toggleColorMode}
          viewMode={viewMode}
          setViewMode={setViewMode}
          primaryColor="#003087"
          secondaryColor="#E31837"
          showFilters={showFilters}
          onToggleFilters={toggleFilters}
        />
        
        <Box sx={{ 
          display: 'flex', 
          flex: 1,
          overflow: 'hidden'
        }}>
          <Sidebar isOpen={isOpen} setIsOpen={setIsOpen} />
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              backgroundColor: 'background.default',
              transition: 'margin 0.2s ease',
              p: 3,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 'calc(100vh - 64px)', // Account for header height
              maxHeight: 'calc(100vh - 64px)', // Prevent overflow
              '& #executive-dashboard': {
                paddingTop: '2px !important',
                '& > *': {
                  paddingTop: '2px !important',
                }
              },
              '& .css-1sf3xto': {
                paddingTop: '2px !important',
              }
            }}
          >
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/propensity" element={<PropensityPredictions />} />
              <Route path="/excel-upload" element={<AdminRoute element={<ExcelUpload />} />} />
              <Route path="/settings" element={<Settings />} />
            </Routes>
          </Box>
        </Box>
      </Box>
    </DashboardViewContext.Provider>
  );
});

// AdminRoute component to restrict access to admin-only routes
interface AdminRouteProps {
  element: React.ReactElement;
}

const AdminRoute = ({ element }: AdminRouteProps) => {
  const { isAdmin } = useAuth();
  const location = useLocation();
  
  if (!isAdmin) {
    // Redirect to dashboard if not an admin
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }
  
  return element;
};

function App() {
  // Add a keyboard listener for debugging
  useEffect(() => {
    // Set up keyboard shortcut (Ctrl+Alt+L) to log the last API call
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+Alt+L
      if (event.ctrlKey && event.altKey && event.key === 'l') {
        logLastApiCall();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  
  return (
    <BrowserRouter>
      <Provider store={store}>
        <AuthProvider>
        <DashboardViewProvider>
          <FilterVisibilityProvider>
              <LoadingProvider>
                <MuiThemeProvider theme={theme}>
                  <MainContent />
                  <LoadingOverlay />
                </MuiThemeProvider>
              </LoadingProvider>
          </FilterVisibilityProvider>
        </DashboardViewProvider>
        </AuthProvider>
      </Provider>
    </BrowserRouter>
  );
}

export default App 
